import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-rainbow-components';
import { TableProps } from 'react-rainbow-components/components/Table';
import { orderBy, OrderByDirection, query } from 'firebase/firestore';
import Pagination from 'components/Pagination';
import Table from 'components/Table';
import DateTimeColumn from 'components/DateTimeColumn';
import useCustomSearchParams from 'hooks/useCustomSearchParams';
import useExternalAutomations from 'data/firestore/agent/externalautomation/useCollectionWithPagination';
import { PAGE_SIZE } from '../../../constants';
import { Container, EntriesText, PaginationContainer } from './styled';
import NameColumn from './columns/name';
import PlatformColumn from './columns/platform';
import ActionsColumn from './columns/actions';

const ExternalAutomationsList = () => {
    const { agentId = '' } = useParams();
    const [searchParams, setSearchParams] = useCustomSearchParams({
        page: {
            type: 'number',
            default: 1,
        },
        sortDirection: {
            type: 'string',
            default: 'desc',
        },
    });

    const { page: activePage, sortDirection = 'desc' } = searchParams;

    const {
        data: externalAutomations,
        isLoading: isLoadingExternalAutomations,
        totalRecords: totalExternalAutomations,
        error,
    } = useExternalAutomations(agentId, {
        disabled: !agentId,
        limit: PAGE_SIZE,
        listQueryFn: (ref) => query(ref, orderBy('createdAt', sortDirection as OrderByDirection)),
        track: [agentId, sortDirection],
    });

    const dataToShow = externalAutomations || [];
    const totalHits = typeof totalExternalAutomations === 'number' ? totalExternalAutomations : 0;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNumber: number) => {
        if (isLoadingExternalAutomations) return;
        setSearchParams({ page: pageNumber });
    };

    const handleOnSort = useCallback(
        (
            event: unknown,
            field: string,
            nextSortDirection: 'asc' | 'desc',
        ) => {
            setSearchParams({ sortDirection: nextSortDirection });
        },
        [setSearchParams],
    );

    if (error || (!externalAutomations && !isLoadingExternalAutomations)) {
        return (
            <Container>
                <h1>Something went wrong</h1>
                {error?.message && (
                    <p>
                        {error.name}
                        :
                        {' '}
                        {error.code}
                    </p>
                )}
            </Container>
        );
    }

    return (
        <Container>
            <Table
                data={dataToShow}
                isLoading={isLoadingExternalAutomations}
                keyField="id"
                sortedBy="createdAt"
                onSort={handleOnSort}
                sortDirection={sortDirection as TableProps['sortDirection']}
            >
                <Column
                    header="Name"
                    component={NameColumn}
                />
                <Column
                    header="Description"
                    field="description"
                />
                <Column
                    header="Platform"
                    field="platform"
                    component={PlatformColumn}
                />
                <Column
                    header="Created At"
                    field="createdAt"
                    component={DateTimeColumn}
                    sortable
                    width={200}
                />
                <Column
                    header="Actions"
                    field="actions"
                    component={ActionsColumn}
                    width={150}
                />
            </Table>
            <PaginationContainer>
                <EntriesText>
                    Showing
                    {' '}
                    {firstItemOfActivePage}
                    {' '}
                    to
                    {' '}
                    {lastItemOfActivePage}
                    {' '}
                    of
                    {' '}
                    {totalHits}
                    {' '}
                    entries.
                </EntriesText>
                <Pagination
                    activePage={activePage}
                    pages={pages}
                    onChange={handlePageChange}
                />
            </PaginationContainer>
        </Container>
    );
};

export default ExternalAutomationsList;
